// Clean 3D airborne routes for the planning map, via a deck.gl overlay — the SAME proven approach the
// single-flight FlightMap uses. MapLibre has no native 3D line and fill-extrusion only makes chunky
// stair-stepped boxes; deck.gl draws:
//   • the route as a thin, smooth, constant-width LineLayer at real altitude, and
//   • the altitude "curtain" as a low-opacity SolidPolygonLayer skirt from the path down to the terrain
//     beneath it (the height cue), NOT a wall of boxes.
// Both interleave with the map so they depth-test correctly against terrain.
//
// Altitude is ANCHORED to the terrain at each flight's take-off point (homeGroundZ + rel_alt) — otherwise a
// launch on a plateau (rel_alt read as MSL) renders the whole path underground. The curtain's BASE follows
// the real terrain under each point, so the skirt meets the ground instead of a flat plane. deck.gl loads
// lazily (already a dependency, already code-split), so it never costs the initial map load.

import type { Map as MapLibreMap } from 'maplibre-gl';
import type { FlightPath } from './engine.ts';
import { plog, ptime } from '../../perf.ts';

/* eslint-disable @typescript-eslint/no-explicit-any -- deck.gl ships loose types; match FlightMap's usage. */
type Deck = { MapboxOverlay: any; LineLayer: any; SolidPolygonLayer: any };
type P3 = [number, number, number];

const hexToRgb = (hex: string): [number, number, number] => {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};

export class FlightDeck {
  private static readonly MAX_PTS = 1500; // per-leg point cap (a single long flight still reads smooth)
  // Hard cap on points across ALL flights combined. Without it, N flights × up to 1500 pts each is unbounded:
  // measured at 177k points / 177k terrain queries / a 40 s render with a large archive. The budget is split
  // across the visible legs so a 100-flight view stays interactive; a 1-flight view still gets full detail.
  private static readonly TOTAL_BUDGET = 8000;
  private readonly map: MapLibreMap;
  private deck: Deck | null = null;
  private overlay: any = null;
  private paths: FlightPath[] = [];
  private air = false; // the airborne-line toggle (in 3D)
  private curtain = false; // the altitude-curtain toggle (in 3D)
  private readonly homeZ = new Map<string, number>(); // flight id → terrain elevation (m) at its take-off
  private terrainQueries = 0; // actual queryTerrainElevation calls in the current render() (post-cache)
  private readonly groundCache = new Map<string, number>(); // memoised terrain elevation by rounded lon,lat
  private renderTimer: ReturnType<typeof setTimeout> | undefined; // coalesces bursty anchor→render calls

  constructor(map: MapLibreMap) {
    this.map = map;
  }

  private get active(): boolean {
    return this.air || this.curtain;
  }

  /** Show/hide the line and the curtain independently. Lazily loads deck.gl on first show. */
  async setVisibility(air: boolean, curtain: boolean): Promise<void> {
    if (air === this.air && curtain === this.curtain) return; // no change → don't rebuild deck layers
    this.air = air;
    this.curtain = curtain;
    if (!this.active) {
      this.overlay?.setProps({ layers: [] });
      return;
    }
    await this.ensureOverlay();
    this.render();
  }
  setPaths(paths: FlightPath[]): void {
    this.paths = paths;
    this.homeZ.clear(); // re-anchor the new flights on the next terrain read
    this.groundCache.clear(); // new geometry → cached terrain elevations are for the old points
    if (this.active) {
      this.anchor();
      this.scheduleRender();
    }
  }
  // Coalesce renders: anchor() fires on every terrain-idle, which comes in bursts while panning. Without
  // this, each burst triggered a full (potentially multi-second) curtain rebuild per idle → the pile-up that
  // froze the UI. One debounced render per burst instead.
  private scheduleRender(): void {
    clearTimeout(this.renderTimer);
    this.renderTimer = setTimeout(() => this.render(), 120);
  }
  /** Re-read terrain elevation under each flight's home (call once the DEM has loaded, e.g. on map idle). */
  // Returns true if any flight's home terrain wasn't readable yet (DEM tile not decoded) — the caller should
  // re-anchor shortly. Critical: a null read must NOT be baked as 0, or a plateau launch (home at 1400 m) draws
  // the whole airborne route at sea level, i.e. under the terrain → the route + curtain "disappear".
  anchor(): boolean {
    let pending = false;
    ptime('FlightDeck.anchor', () => {
      let changed = false;
      for (const p of this.paths) {
        const home = p.legs.find((l) => l.length)?.[0];
        if (!home) continue;
        const z = this.map.queryTerrainElevation([home[0], home[1]]);
        if (z == null || !Number.isFinite(z)) { pending = true; continue; } // tile not ready — retry, don't bake 0
        if (this.homeZ.get(p.id) !== z) {
          this.homeZ.set(p.id, z);
          changed = true;
        }
      }
      // homeZ shifts only the curtain TOP (hz + rel_alt); the per-point BASE terrain in groundCache is
      // unaffected, so re-render but keep the cache (re-querying 8k points on every pan tick was the freeze).
      if (changed && this.active) this.scheduleRender();
    });
    return pending;
  }
  dispose(): void {
    clearTimeout(this.renderTimer);
    if (this.overlay) {
      this.map.removeControl(this.overlay as never);
      this.overlay.finalize?.();
      this.overlay = null;
    }
  }

  private async ensureOverlay(): Promise<void> {
    if (this.overlay) return;
    // First 3D activation lazy-loads deck.gl. Split-time the import (chunk download + PARSE — ~750 KB of JS,
    // a real main-thread cost the first time) from the overlay/WebGL init, to see which owns the one-time
    // multi-second stall when flights first appear in 3D.
    const ti = plog('deck: importing @deck.gl chunks (first 3D)');
    const [mb, ly] = await Promise.all([import('@deck.gl/mapbox'), import('@deck.gl/layers')]);
    const tinit = plog('deck: chunks loaded → init overlay', ti);
    this.deck = { MapboxOverlay: mb.MapboxOverlay, LineLayer: ly.LineLayer, SolidPolygonLayer: ly.SolidPolygonLayer };
    // interleaved → deck draws INTO the map's GL pass, so line + curtain depth-test against terrain correctly.
    this.overlay = new this.deck.MapboxOverlay({ interleaved: true, layers: [] });
    this.map.addControl(this.overlay as never);
    plog('deck: overlay ready (WebGL up)', tinit);
    this.anchor();
  }

  // Terrain elevation under a point (m), falling back to the flight's home when the DEM isn't ready yet.
  private groundAt(lon: number, lat: number, fallback: number): number {
    const key = `${lon.toFixed(4)},${lat.toFixed(4)}`;
    const cached = this.groundCache.get(key);
    if (cached !== undefined) return cached; // repeated renders (pan bursts) reuse the elevation, not re-query
    this.terrainQueries++;
    const z = this.map.queryTerrainElevation([lon, lat]);
    const v = z == null || !Number.isFinite(z) ? fallback : z;
    this.groundCache.set(key, v);
    return v;
  }

  private render(): void {
    if (!this.deck || !this.overlay) return;
    clearTimeout(this.renderTimer); // this IS the render — cancel any pending debounced one
    const t = performance.now();
    this.terrainQueries = 0;
    // Split the global point budget across every visible leg so the TOTAL work is bounded regardless of how
    // many flights are shown. One flight → full detail (MAX_PTS); a hundred flights → ~80 pts each, still a
    // readable ribbon, but ~8k points total instead of 177k.
    // Per-leg cap = budget / legs, but never below a small floor (so a short flight still reads as a ribbon)
    // and never above MAX_PTS (so one long flight stays smooth). The floor MUST stay small: at 24 it dominated
    // when many legs were shown (653 legs × 24 = 15k, 2× over budget → a 1 s render); 6 keeps the budget the
    // real bound (653 × ~12 ≈ 8k) while a lone flight still gets MAX_PTS.
    const legCount = this.paths.reduce((n, p) => n + p.legs.filter((l) => l.length >= 2).length, 0);
    const perLegCap = Math.min(FlightDeck.MAX_PTS, Math.max(6, Math.floor(FlightDeck.TOTAL_BUDGET / Math.max(1, legCount))));
    const segs: Array<{ from: P3; to: P3; color: [number, number, number] }> = [];
    const skirt: Array<{ polygon: P3[]; color: [number, number, number] }> = [];
    for (const p of this.paths) {
      const hz = this.homeZ.get(p.id) ?? 0;
      const color = hexToRgb(p.color);
      for (const leg of p.legs) {
        if (leg.length < 2) continue;
        // Downsample: a long flight has tens of thousands of samples — straight segments between ~1500 of
        // them still read as a smooth line, but keep every segment/polygon (and every terrain read) uncapped
        // and deck.gl janks. Same reason FlightMap caps at ~2000.
        const step = Math.max(1, Math.ceil(leg.length / perLegCap));
        const idx: number[] = [];
        for (let i = 0; i < leg.length; i += step) idx.push(i);
        if (idx[idx.length - 1] !== leg.length - 1) idx.push(leg.length - 1);
        // Precompute top + base per KEPT point once — the base terrain read is the costly bit, and each
        // interior point was previously read twice (as both ends of adjacent segments).
        const top: P3[] = [];
        const base: P3[] = [];
        for (const i of idx) {
          const a = leg[i];
          top.push([a[0], a[1], hz + a[2]]);
          if (this.curtain) base.push([a[0], a[1], this.groundAt(a[0], a[1], hz)]);
        }
        for (let k = 0; k < top.length - 1; k++) {
          if (!Number.isFinite(top[k][2]) || !Number.isFinite(top[k + 1][2])) continue; // bad-alt telemetry
          if (this.air) segs.push({ from: top[k], to: top[k + 1], color });
          if (this.curtain) skirt.push({ polygon: [top[k], top[k + 1], base[k + 1], base[k]], color });
        }
      }
    }
    const layers: any[] = [];
    // Curtain first (under the line). Low alpha so it reads as a translucent height cue, both sides drawn.
    if (this.curtain) {
      layers.push(
        new this.deck.SolidPolygonLayer({
          id: 'plan-flight-curtain',
          data: skirt,
          getPolygon: (d: any) => d.polygon,
          getFillColor: (d: any) => [d.color[0], d.color[1], d.color[2], 55],
          _full3d: true,
          extruded: false,
          pickable: false,
          parameters: { cullMode: 'none' },
        }),
      );
    }
    if (this.air) {
      layers.push(
        new this.deck.LineLayer({
          id: 'plan-flight-3d',
          data: segs,
          getSourcePosition: (d: any) => d.from,
          getTargetPosition: (d: any) => d.to,
          getColor: (d: any) => d.color,
          getWidth: 3,
          widthUnits: 'pixels',
          widthMinPixels: 2,
        }),
      );
    }
    this.overlay.setProps({ layers });
    // The 3D-freeze probe: report a render that's heavy enough to stutter, with the two costs that scale —
    // terrain reads (queryTerrainElevation, the curtain's per-point ground lookup) and polygon/segment count.
    const d = performance.now() - t;
    if (d >= 30) plog(`SLOW FlightDeck.render ${Math.round(d)}ms — ${this.terrainQueries} terrain-queries, ${skirt.length} skirt polys, ${segs.length} segs`);
  }
}
